const TOKEN_KEY = 'authToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

/**
 * Saves the authentication token to local storage.
 * @param {string} token The authentication token.
 */
export const saveAuthToken = (token) => {
  if (typeof window !== 'undefined') {
    console.log('Saving token to localStorage:', token);
    localStorage.setItem(TOKEN_KEY, token);
  }
};

/**
 * Saves the refresh token to local storage.
 * @param {string} token The refresh token.
 */
export const saveRefreshToken = (token) => {
  if (typeof window !== 'undefined') {
    console.log('Saving refresh token to localStorage');
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  }
};

/**
 * Retrieves the authentication token from local storage.
 * @returns {string | null} The authentication token or null if not found.
 */
export const getAuthToken = () => {
  if (typeof window !== 'undefined') {
    const token = localStorage.getItem(TOKEN_KEY);
    console.log('Retrieved token from localStorage:', token);
    return token;
  }
  return null;
};

/**
 * Retrieves the refresh token from local storage.
 * @returns {string | null} The refresh token or null if not found.
 */
export const getRefreshToken = () => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }
  return null;
};

/**
 * Removes the authentication token from local storage.
 */
export const removeAuthToken = () => {
  if (typeof window !== 'undefined') {
    console.log('Removing token from localStorage');
    localStorage.removeItem(TOKEN_KEY);
  }
};

/**
 * Removes the refresh token from local storage.
 */
export const removeRefreshToken = () => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }
};

/**
 * Checks if a user is currently authenticated (token exists).
 * @returns {boolean} True if a token exists, false otherwise.
 */
export const isAuthenticated = () => {
  return !!getAuthToken();
};

/**
 * Parses a JWT token to get its payload.
 * @param {string} token The JWT token to parse.
 * @returns {Object|null} The decoded payload or null if invalid.
 */
export const parseJwt = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error parsing JWT token:', error);
    return null;
  }
};

/**
 * Checks if the current token is expired.
 * @param {number} buffer Optional time buffer in seconds to consider a token as expired. Default is 60s.
 * @returns {boolean} True if token is expired, invalid, or about to expire; false otherwise.
 */
export const isTokenExpired = (buffer = 60) => {
  const token = getAuthToken();
  if (!token) return true;
  
  try {
    const payload = parseJwt(token);
    if (!payload || !payload.exp) return true;
    
    // Check if token is expired or will expire within buffer time
    // (exp is in seconds, Date.now() is in milliseconds)
    // Add a buffer time to refresh token before it actually expires
    return payload.exp * 1000 < (Date.now() + buffer * 1000);
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true; // Consider invalid tokens as expired
  }
};

/**
 * Returns the authorization header value for API requests.
 * @returns {string} The formatted authorization header for Django REST Framework Token auth.
 */
export const getAuthHeader = () => {
  const token = getAuthToken();
  return token ? `Bearer ${token}` : '';
}; 